import {
    Component, Output, OnInit, OnDestroy, EventEmitter, ViewChild, AfterViewInit, OnChanges, SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';

import { MenuItem } from 'primeng/api';

import { HisNotificationService, HisConfirmationService, GenericValue } from 'hisng';

import { PublicationCreatorWizardNameComponent } from './publication-creator-wizard-name.component';
import { PublicationCreator } from 'app/fs/res/publication/model/publication-creator';
import { HisBaseGlobalsService, HisUiService } from 'hisng';
import { CreatorType } from 'app/fs/res/publication/model/enums/creator-type-enum';

import { CREATOR_ADD, CREATOR_KEY, ORIGINAL_CREATOR_KEY } from 'app/fs/res/publication/model/constants';
import { CONFIRMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/confirmation-dialog.component';

const creatornameFormKey = 'creatorname';
const firstnameFormKey = 'firstname';
const nameprefixFormKey = 'nameprefix';
const namesuffixFormKey = 'namesuffix';

@Component( {
    selector: 'res-publication-creator-wizard',
    templateUrl: './publication-creator-wizard.component.html',
} )
export class PublicationCreatorWizardComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Output() applyChangedCreator = new EventEmitter<PublicationCreator>();
    @Output() originalCreatorEmitter = new EventEmitter<PublicationCreator>();

    public creator: PublicationCreator;
    public creatorType: GenericValue;
    public display = false;
    public unchanged = true;

    public menuItems: MenuItem[] = [];
    public activeIndex: number;

    private formSubscription: Subscription;

    get wizardTitleKey() {
        const isAuthor = CreatorType.authorsAsPerson().findIndex( c => c === this.creatorType?.hiskeyId ) > -1;
        const isEditor = CreatorType.editorsAsPerson().findIndex( c => c === this.creatorType?.hiskeyId ) > -1;
        return isAuthor ? 'fs.res.publication.creators.author.wizard.title' : isEditor ?
            'fs.res.publication.creators.editor.wizard.title' : '';
    }

    @ViewChild( PublicationCreatorWizardNameComponent, { static: false } ) wizardNamePage: PublicationCreatorWizardNameComponent;

    constructor(
        private notificationService: HisNotificationService,
        private confirmationService: HisConfirmationService,
        private hisUiService: HisUiService,
        private hisBaseGlobalsService: HisBaseGlobalsService
    ) { }

    ngOnInit() {
        this.creator = new PublicationCreator();
        this.menuItems = [
            { label: this.hisUiService.translate( 'fs.res.publication.creators.wizard.step1.title' ) },
            { label: this.hisUiService.translate( 'fs.res.publication.creators.wizard.step2.title' ) },
            { label: this.hisUiService.translate( 'fs.res.publication.creators.wizard.step3.title' ) }
        ];
        this.activeIndex = 0;
    }

    ngAfterViewInit() {
        if ( this.wizardNamePage ) {
            this.formSubscription = this.wizardNamePage.creatorNameForm.valueChanges.subscribe( ( nameChanges: PublicationCreator ) => {
                console.log( 'DEBUGG::NameChanges', nameChanges );
                if ( this.wizardNamePage.creatorNameForm.dirty ) {
                    if ( this.wizardNamePage.isEditCreator() ) {
                        this.wizardNamePage.showNameChangeWarning = true;
                        if ( !this.isCreatorEqualOriginal( nameChanges ) ) {
                            this.unchanged = false;
                        }
                    }
                }
                if ( this.wizardNamePage.creatorNameForm.pristine ) {
                    this.unchanged = true;
                }
            } );
        }
    }

    ngOnChanges( changes: SimpleChanges ) {
        let changesKey = 'creator';
        if ( changes[changesKey].currentValue.creatorname !== undefined ) {
            this.setFormValuesFromCreator();
        }
    }

    ngOnDestroy() {
        this.formSubscription.unsubscribe();
    }

    creatorChanged( event: PublicationCreator ) {
        console.log( 'DEBUGG::changedCreator', event );
        this.creator = event;
        if ( !this.isCreatorEqualOriginal( this.creator ) ) {
            this.unchanged = false;
        }
    }
    // eslint-disable-next-line no-trailing-spaces
    
    creatorChangeConfirmed( event: PublicationCreator ) {
        console.log( 'DEBUGG::creatorChangeConfirmed', event );
        if(this.creator.creatorname?.trim().length > 0 && this.creator.firstname?.trim().length > 0) {
            this.confirmNext();
        }

    }

    saveCreator() {
        this.applyChangedCreator.emit( this.creator );
        this.hideDialog();
    }

    // navigation methods ##################################################

    confirmNext() {
        if ( this.unchanged ) {
            this.getNextStep( true );
        } else {
            this.confirmationDialogShow();
        }
    }

    confirmationDialogShow() {

        this.confirmationService.confirm( {
            key: CONFIRMATION_PDIALOG_KEY,
            header: this.hisUiService.translate(
                'fs.res.common.confirmation.warn',
            ),
            acceptLabel: this.hisUiService.translate(
                'wizard.next',
            ),
            rejectLabel: this.hisUiService.translate(
                'wizard.reset',
            ),
            message: this.hisUiService.translate(
                'fs.res.publication.creators.wizard.nameChangeWarning',
            ),
            reject: () => {
                this.unchanged = true;
                this.resetCreatorData();
            },
            accept: () => {
                this.changeConfirmation();
                this.getNextStep( true );
                this.unchanged = false;
                localStorage.setItem( ORIGINAL_CREATOR_KEY, JSON.stringify( this.creator ) );
            }
        } );
    }

    changeConfirmation() {
        this.setCreatorNameFromFormValues();
        this.resetCreatorPersonAndOrganization();
    }

    public getNextStep( isNext: boolean ) {
        if ( this.activeIndex === 0 && !this.areFieldsValid() ) {
            return;
        }

        if ( isNext ) {
            if ( this.formSubscription ) {
                this.formSubscription.unsubscribe();
            }
            this.activeIndex++;
        } else {
            this.activeIndex--;
        }
    }

    getLastStep() {
        if ( this.activeIndex === 0 && !this.areFieldsValid() ) {
            return;
        }
        if ( !this.isNewCreator() && this.unchanged ) {
            this.activeIndex = this.menuItems.length - 1;
        }
    }

    // dialog methods #############################################################

    showDialog() {
        this.display = true;
    }

    hideDialog() {
        this.display = false;
        this.unchanged = true;
        localStorage.removeItem( CREATOR_KEY );
        localStorage.removeItem( ORIGINAL_CREATOR_KEY );
    }

    onHide() {
        this.creator = new PublicationCreator();
        this.activeIndex = 0;
        this.unchanged = true;
        this.formSubscription.unsubscribe();
        if ( this.wizardNamePage ) {
            this.wizardNamePage.creatorNameForm.reset();
        }
        localStorage.removeItem( CREATOR_KEY );
        localStorage.removeItem( ORIGINAL_CREATOR_KEY );
    }

    isNewCreator() {
        let isNewCreator = false;
        let creatorStatus = localStorage.getItem( CREATOR_KEY );
        if ( creatorStatus && creatorStatus === CREATOR_ADD ) {
            isNewCreator = true;
        }
        return isNewCreator;

    }

    private areFieldsValid(): boolean {
        // This regEx is similar to backend-validation, but not the same (java-pattern is not compatible with javaScript)
        // eslint-disable-next-line max-len
        const regexToValidatePersonName = /(?=.{2,})(?:^[a-zA-ZÖÄÜöäüß’'´`\u00C0-\u00FF\u0100-\u017F\u0180-\u024F]+\.?([ \-]?[a-zA-ZÖÄÜöäüß’'´`\u00C0-\u00FF\u0100-\u017F\u0180-\u024F]+\.?)*$)/;
        this.notificationService.clear();
        let firstname = this.creator?.firstname?.trim();
        let creatorname = this.creator?.creatorname?.trim();
        let areFieldsValid = true;
        let inputRequired = this.hisUiService.translate( 'validation.required' );
        let inputFirstname = this.hisUiService.translate( 'de.his.javang.person.firstName' );
        let inputCreatorname = this.hisUiService.translate( 'de.his.javang.person.lastName' );
        let message = [];
        if ( this.firstNameIsRequired() ) {
            if ( !firstname ) {
                areFieldsValid = false;
                message.push( `${this.hisUiService.translate( 'de.his.javang.person.firstName' )}: ${inputRequired}` );
            } else if ( !regexToValidatePersonName.test( firstname ) ) {
                areFieldsValid = false;
                message.push( `${this.hisUiService.translate( 'error.psv.Person.invalidName', inputFirstname )}` );
            }
        }
        if ( !creatorname ) {
            areFieldsValid = false;
            message.push( `${this.hisUiService.translate( 'de.his.javang.person.lastName' )}: ${inputRequired}` );
        } else if ( !regexToValidatePersonName.test( creatorname ) ) {
            areFieldsValid = false;
            message.push( `${this.hisUiService.translate( 'error.psv.Person.invalidName', inputCreatorname )}` );
        }
        if ( !areFieldsValid ) {
            this.notificationService.error(
                message,
                this.hisUiService.translate( 'global.validationerror' )
            );
        }
        return areFieldsValid;
    }

    private firstNameIsRequired(): boolean {
        let entry = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationHumanCreator.firstName' );
        if ( !entry ) {
            /* first name is required by default
             * nevertheless, the dd key should be available, so this should not happen
            */
            return true;
        }

        // Es gilt required === null, wenn "Erforderlich: nein" eingestellt ist
        return entry.required === true;
    }

    private resetCreatorPersonAndOrganization() {
        this.creator.person = null;
        this.creator.creatorOrganizations = [];
    }

    private setCreatorNameFromFormValues() {
        this.creator.creatorname = this.wizardNamePage.creatorNameForm.controls[creatornameFormKey].value;
        this.creator.firstname = this.wizardNamePage.creatorNameForm.controls[firstnameFormKey].value;
        this.creator.nameprefix = this.wizardNamePage.creatorNameForm.controls[nameprefixFormKey].value;
        this.creator.namesuffix = this.wizardNamePage.creatorNameForm.controls[namesuffixFormKey].value;
    }

    private setFormValuesFromCreator() {
        this.wizardNamePage.creatorNameForm.controls[creatornameFormKey].setValue( this.creator.creatorname );
        this.wizardNamePage.creatorNameForm.controls[firstnameFormKey].setValue( this.creator.firstname );
        if ( this.creator.nameprefix ) {
            this.wizardNamePage.creatorNameForm.controls[nameprefixFormKey].setValue( this.creator.nameprefix );
        } else {
            this.wizardNamePage.creatorNameForm.controls[nameprefixFormKey].setValue( '' );
        }
        if ( this.creator.namesuffix ) {
            this.wizardNamePage.creatorNameForm.controls[namesuffixFormKey].setValue( this.creator.namesuffix );
        } else {
            this.wizardNamePage.creatorNameForm.controls[namesuffixFormKey].setValue( '' );
        }
    }

    private isCreatorEqualOriginal( creatorToCheck: PublicationCreator ) {
        let originalCreator = JSON.parse( localStorage.getItem( ORIGINAL_CREATOR_KEY ) );

        if ( !originalCreator ) {
            return true;
        }

        originalCreator.nameprefix = !originalCreator.namesuffix ? '' : originalCreator.namesuffix;
        originalCreator.namesuffix = !originalCreator.namesuffix ? '' : originalCreator.namesuffix;

        return ( creatorToCheck.creatorname === originalCreator.creatorname &&
            creatorToCheck.firstname === originalCreator.firstname &&
            creatorToCheck.nameprefix === originalCreator.nameprefix &&
            creatorToCheck.namesuffix === originalCreator.namesuffix );
    }

    private resetCreatorData() {
        let originalCreator = JSON.parse( localStorage.getItem( ORIGINAL_CREATOR_KEY ) );
        if ( originalCreator ) {
            this.creator = Object.assign( {}, originalCreator );
        }
    }

}
